import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaEdit, FaEye, FaDownload, FaTrash, FaCheck, FaTimes, FaSpinner, FaMicrophone, FaPlay, FaPause, FaCog, FaKey } from 'react-icons/fa';
import LanguageSwitcher from '../components/LanguageSwitcher';
import './Gallery.css';

// Axios instance oluşturalım
const isDevelopment = process.env.NODE_ENV === 'development';
const axiosInstance = axios.create({
  baseURL: isDevelopment ? 'http://localhost:3001' : '', // Development ortamında localhost:3001, production'da relatif path
  timeout: 30000,
  withCredentials: true, // Important for sending cookies
  headers: {
    'Connection': 'keep-alive'
  }
});

// Separate axios instance for bulk downloads with longer timeout
const bulkDownloadAxios = axios.create({
  baseURL: isDevelopment ? 'http://localhost:3001' : '',
  timeout: 1800000, // 30 minutes timeout for chunked downloads
  withCredentials: true, // Important for sending cookies
  headers: {
    'Connection': 'keep-alive'
  }
});

function App({ hashFromAuth }) {
  const [mediaList, setMediaList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [downloadingMedia, setDownloadingMedia] = useState(null);
  const [isBulkDownloading, setIsBulkDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [isDarkTheme, setIsDarkTheme] = useState(false);
  const [showCelebrationModal, setShowCelebrationModal] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const [weddingInfo, setWeddingInfo] = useState(null);
  const [mediaType, setMediaType] = useState('images'); // 'images', 'videos', 'audios'
  const [audioPlayingId, setAudioPlayingId] = useState(null);
  const [audioElements, setAudioElements] = useState({});
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordChangeMessage, setPasswordChangeMessage] = useState({ type: '', text: '' });
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const { hash: hashFromParams } = useParams(); // Get hash from URL params
  const hash = hashFromAuth || hashFromParams; // Use auth hash if available, fallback to URL
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Special font for specific wedding
  const specialFontHashes = ['31395'];
  const useGilroy = specialFontHashes.includes(hash);

  // Extract bride and groom names from wedding path
  const getBrideGroomNames = useCallback((weddingPath) => {
    if (!weddingPath) return '';
    
    // Helper function to split name by capital letters
    const splitByCapitals = (name) => {
      if (!name || name.length <= 1) return name;
      return name.replace(/([A-Z])/g, ' $1').trim();
    };
    
    // Wedding path format: "brideName-groomName"
    const parts = weddingPath.split('-');
    if (parts.length >= 2) {
      // Skip any numeric parts (for backwards compatibility with old format)
      const nameParts = parts.filter(part => isNaN(part) && part.length > 2);
      if (nameParts.length >= 2) {
        // Process bride name with capital letter splitting
        const brideRaw = nameParts[0].charAt(0).toUpperCase() + nameParts[0].slice(1);
        const bride = splitByCapitals(brideRaw);
        
        // Process groom name with capital letter splitting
        const groomRaw = nameParts[1].charAt(0).toUpperCase() + nameParts[1].slice(1);
        const groom = splitByCapitals(groomRaw);
        
        return `${bride} & ${groom}`;
      } else if (nameParts.length === 1) {
        const nameRaw = nameParts[0].charAt(0).toUpperCase() + nameParts[0].slice(1);
        const name = splitByCapitals(nameRaw);
        return name;
      }
    }
    return weddingPath;
  }, []);

  const fetchWeddingInfo = useCallback(async () => {
    try {
      if (!hash) {
        setWeddingInfo(null);
        return;
      }

      const response = await axiosInstance.get(`/api/admin/wedding-info/${hash}`);
      setWeddingInfo(response.data.data);
    } catch (error) {
      console.error('Error fetching wedding info:', error);
      
      // If authentication required (401), redirect to login
      if (error.response && error.response.status === 401) {
        console.log('Authentication required, redirecting to login...');
        setLoading(false);
        navigate('/camera-roll/login', { replace: true });
        return;
      }
      
      // If wedding not found (404), redirect to home page
      if (error.response && error.response.status === 404) {
        console.log('Wedding not found, redirecting to home page...');
        setLoading(false); // Stop loading before redirect
        navigate('/', { replace: true }); // Use replace to avoid back button issues
        return;
      }
      
      setWeddingInfo(null);
    }
  }, [hash, navigate]);

  const fetchMedia = useCallback(async () => {
    try {
      if (!hash) {
        // If no hash is provided, show empty list and no error
        setMediaList([]);
        setLoading(false);
        setIsFirstLoad(false);
        return;
      }
      
      // Use different endpoints based on media type
      let endpoint = `/api/media/admin/${hash}/images`;
      if (mediaType === 'images') {
        endpoint = `/api/media/admin/${hash}/images`;
      } else if (mediaType === 'videos') {
        endpoint = `/api/media/admin/${hash}/videos`;
      } else if (mediaType === 'audios') {
        endpoint = `/api/media/admin/${hash}/audios`;
      }
      
      const response = await axiosInstance.get(endpoint);
      

      setMediaList(response.data.data);
      
      // Show celebration modal on first load if there are media files
      if (isFirstLoad && response.data.data.length > 0) {
        setShowCelebrationModal(true);
        // Hide modal after 4 seconds
        setTimeout(() => {
          setShowCelebrationModal(false);
        }, 4000);
      }
      
      setLoading(false);
      setIsFirstLoad(false);
    } catch (err) {
      console.error("Error fetching media:", err);
      
      // If authentication required (401), redirect to login
      if (err.response && err.response.status === 401) {
        console.log('Authentication required, redirecting to login...');
        setLoading(false);
        navigate('/camera-roll/login', { replace: true });
        return;
      }
      
      // If wedding not found (404), redirect to home page
      if (err.response && err.response.status === 404) {
        console.log('Media endpoint returned 404, redirecting to home page...');
        setLoading(false); // Stop loading before redirect
        navigate('/', { replace: true }); // Use replace to avoid back button issues
        return;
      }
      
      setError(t('admin.error'));
      setLoading(false);
      setIsFirstLoad(false);
    }
  }, [hash, t, isFirstLoad, navigate, mediaType]);

  useEffect(() => {
    fetchWeddingInfo();
    fetchMedia();
  }, [fetchWeddingInfo, fetchMedia]);

  // Handle body scroll when modal is open
  useEffect(() => {
    if (showCelebrationModal) {
      // Store current scroll position
      const scrollY = window.scrollY;
      
      // Add modal-open class and prevent scroll
      document.body.classList.add('modal-open');
      document.body.style.top = `-${scrollY}px`;
      
      // Prevent touch scroll on mobile
      const preventScroll = (e) => {
        e.preventDefault();
      };
      
      document.addEventListener('touchmove', preventScroll, { passive: false });
      document.addEventListener('wheel', preventScroll, { passive: false });
      
      return () => {
        // Cleanup scroll prevention
        document.removeEventListener('touchmove', preventScroll);
        document.removeEventListener('wheel', preventScroll);
      };
    } else {
      // Restore scroll
      const scrollY = document.body.style.top;
      document.body.classList.remove('modal-open');
      document.body.style.top = '';
      
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }
  }, [showCelebrationModal]);

  // Cleanup modal state on unmount
  useEffect(() => {
    return () => {
      document.body.classList.remove('modal-open');
      document.body.style.top = '';
    };
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm(t('admin.confirmDelete'))) {
      try {
        await axiosInstance.delete(`/api/media/${id}`);
        setMediaList(mediaList.filter(media => media.id !== id));
      } catch (err) {
        setError(t('admin.deleteError'));
      }
    }
  };

  const handleBulkDelete = async () => {
    if (selectedMedia.length === 0) return;
    
    if (window.confirm(t('admin.confirmBulkDelete', { count: selectedMedia.length }))) {
      try {
        await axiosInstance.post('/api/media/bulk-delete', {
          ids: selectedMedia
        });
        setMediaList(mediaList.filter(media => !selectedMedia.includes(media.id)));
        setSelectedMedia([]);
      } catch (err) {
        setError(t('admin.bulkDeleteError'));
      }
    }
  };

  const handleDownload = async (id) => {
    try {
      setDownloadingMedia(id);
      const response = await axiosInstance.get(`/api/media/download/${id}`, {
        responseType: 'blob'
      });
      
      // Get the content type from response headers
      const contentType = response.headers['content-type'] || 'application/octet-stream';
      
      // Create a blob URL with the correct MIME type
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from Content-Disposition header if available
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'download';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

    } catch (err) {
      console.error(`[${new Date().toISOString()}] Download failed for media ID: ${id}`, err);
      setError(t('admin.downloadError'));
    } finally {
      setDownloadingMedia(null);
    }
  };

  const handleBulkDownload = async () => {
    if (selectedMedia.length === 0) return;
    
    // Remove the file limit check - let backend handle chunking automatically
    
    // Show helpful suggestion for large downloads (300+ files)
    if (selectedMedia.length > 300) {
      const confirmed = window.confirm(t('admin.veryLargeDownloadWarning', { 
        count: selectedMedia.length 
      }));
      if (!confirmed) return;
    }
    
    try {
      setIsBulkDownloading(true);
      setDownloadProgress(0);
      console.log(`[${new Date().toISOString()}] Starting automatic chunked download for ${selectedMedia.length} files`);

      const response = await bulkDownloadAxios.post('/api/media/bulk-download-chunked', {
        ids: selectedMedia
      }, {
        responseType: 'blob',
        onDownloadProgress: (progressEvent) => {
          if (progressEvent.lengthComputable) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setDownloadProgress(percentCompleted);
            console.log(`[${new Date().toISOString()}] Download progress: ${percentCompleted}%`);
          } else {
            // For chunked downloads, estimate progress based on received data and file count
            const totalFiles = selectedMedia.length;
            const estimatedAverageFileSize = 3 * 1024 * 1024; // 3MB average
            const estimatedTotalSize = totalFiles * estimatedAverageFileSize;
            
            const estimatedProgress = Math.min(95, Math.round((progressEvent.loaded / estimatedTotalSize) * 100));
            setDownloadProgress(estimatedProgress);
            
            console.log(`[${new Date().toISOString()}] Estimated progress: ${estimatedProgress}% (${(progressEvent.loaded / (1024 * 1024)).toFixed(1)}MB received)`);
          }
        }
      });
      
      // Set progress to 100% when download completes
      setDownloadProgress(100);
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const zipFileName = `wedding-media-${Date.now()}.zip`;
      link.setAttribute('download', zipFileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      console.log(`[${new Date().toISOString()}] Automatic chunked download completed successfully`);
      
      // Show success message briefly
      setTimeout(() => {
        setDownloadProgress(0);
      }, 2000);
      
    } catch (err) {
      console.error(`[${new Date().toISOString()}] Chunked download failed for ${selectedMedia.length} files:`, err);
      
      // More specific error messages
      if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
        setError(t('admin.downloadTimeoutError'));
      } else {
      setError(t('admin.bulkDownloadError'));
      }
    } finally {
      setIsBulkDownloading(false);
      // Don't reset progress immediately, let it show 100% for a moment
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedMedia(mediaList.map(media => media.id));
    } else {
      setSelectedMedia([]);
    }
  };

  const handleSelectMedia = (id) => {
    if (selectedMedia.includes(id)) {
      setSelectedMedia(selectedMedia.filter(mediaId => mediaId !== id));
    } else {
      setSelectedMedia([...selectedMedia, id]);
    }
  };

  // Handle media type change
  const handleMediaTypeChange = (type) => {
    if (type !== mediaType) {
      setLoading(true);
      setMediaType(type);
      setSelectedMedia([]); // Clear selection when changing tabs
    }
  };

  // Audio playback controls
  const toggleAudioPlay = (mediaId, audioUrl) => {
    if (audioPlayingId === mediaId) {
      // Pause current audio
      if (audioElements[mediaId]) {
        audioElements[mediaId].pause();
      }
      setAudioPlayingId(null);
    } else {
      // Pause any currently playing audio
      if (audioPlayingId && audioElements[audioPlayingId]) {
        audioElements[audioPlayingId].pause();
      }
      
      // Play new audio
      let audio = audioElements[mediaId];
      if (!audio) {
        audio = new Audio(audioUrl);
        audio.onended = () => setAudioPlayingId(null);
        setAudioElements(prev => ({ ...prev, [mediaId]: audio }));
      }
      audio.play();
      setAudioPlayingId(mediaId);
    }
  };

  return (
    <div className={`min-h-screen transition-all duration-500 ${useGilroy ? 'gilroy-font' : ''} ${isDarkTheme 
      ? 'bg-gradient-to-br from-slate-950 via-blue-950 to-indigo-950' 
      : 'bg-gradient-to-br from-stone-50 via-amber-50 to-orange-100'
    }`}>
      
      {/* Modern Header */}
      <header className={`backdrop-blur-md border-b sticky top-0 z-40 shadow-sm transition-all duration-300 ${isDarkTheme
        ? 'bg-slate-950/70 border-slate-800/50'
        : 'bg-white/70 border-stone-200/50'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center py-4 sm:py-6 gap-4 sm:gap-0">
            <div className="flex items-center gap-3 sm:gap-4 w-full sm:w-auto">
              {/* Logo */}
              <img 
                src="/new-logo.png" 
                alt="Weddingram Logo" 
                className="h-10 sm:h-12 w-auto drop-shadow-lg hover:scale-105 transition-transform duration-200 flex-shrink-0"
              />
              <div className="min-w-0 flex-1 sm:flex-initial">
                <h1 className={`text-xl sm:text-2xl font-bold bg-gradient-to-r bg-clip-text text-transparent transition-all duration-300 ${isDarkTheme
                  ? 'from-white to-gray-300'
                  : 'from-gray-800 to-gray-600'
                }`}>
                   {weddingInfo && (
                    <span className={`text-base sm:text-lg font-medium ${isDarkTheme ? 'text-pink-200' : 'text-pink-400'}`}>
                      {getBrideGroomNames(weddingInfo.weddingPath)}
                    </span>
                  )}
                </h1>
                <p className={`text-xs sm:text-sm transition-all duration-300 ${isDarkTheme ? 'text-gray-400' : 'text-gray-600'}`}>{t('admin.subtitle')}</p>
              </div>
            </div>
            
            {/* Theme Toggle Button */}
            <div className="flex items-center gap-3 sm:gap-4 self-end sm:self-auto">
              <button
                onClick={() => setIsDarkTheme(!isDarkTheme)}
                className={`w-9 h-9 sm:w-10 sm:h-10 backdrop-blur-sm rounded-xl flex items-center justify-center transition-all duration-200 shadow-sm hover:shadow-md ${isDarkTheme
                  ? 'bg-slate-900/80 hover:bg-slate-800/90 border border-slate-700/60 hover:border-slate-600/60'
                  : 'bg-white/80 hover:bg-white/90 border border-stone-200/60 hover:border-stone-300/60'
                }`}
                title={isDarkTheme ? "Switch to Light Theme" : "Switch to Dark Theme"}
              >
                {isDarkTheme ? (
                  // Sun icon for light theme
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                  </svg>
                ) : (
                  // Moon icon for dark theme
                  <svg className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                  </svg>
                )}
              </button>
              <LanguageSwitcher isDarkTheme={isDarkTheme} />
              
              {/* Settings Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowSettingsDropdown(!showSettingsDropdown)}
                  className={`w-9 h-9 sm:w-10 sm:h-10 backdrop-blur-sm rounded-xl flex items-center justify-center transition-all duration-200 shadow-sm hover:shadow-md ${isDarkTheme
                    ? 'bg-slate-900/80 hover:bg-slate-800/90 border border-slate-700/60 hover:border-slate-600/60'
                    : 'bg-white/80 hover:bg-white/90 border border-stone-200/60 hover:border-stone-300/60'
                  }`}
                  title="Settings"
                >
                  <FaCog className={`w-4 h-4 sm:w-5 sm:h-5 ${isDarkTheme ? 'text-slate-300' : 'text-slate-600'}`} />
                </button>
                
                {/* Dropdown Menu */}
                {showSettingsDropdown && (
                  <>
                    {/* Backdrop to close dropdown */}
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setShowSettingsDropdown(false)}
                    />
                    
                    {/* Dropdown content */}
                    <div className={`absolute right-0 mt-2 w-48 rounded-xl shadow-lg z-50 backdrop-blur-sm border ${isDarkTheme
                      ? 'bg-slate-900/95 border-slate-700/60'
                      : 'bg-white/95 border-stone-200/60'
                    }`}>
                      <button
                        onClick={() => {
                          setShowSettingsDropdown(false);
                          setShowChangePasswordModal(true);
                        }}
                        className={`w-full px-4 py-3 text-left flex items-center gap-3 transition-colors rounded-xl ${isDarkTheme
                          ? 'hover:bg-slate-800/80 text-slate-200'
                          : 'hover:bg-stone-100/80 text-slate-700'
                        }`}
                      >
                        <FaKey className="w-4 h-4" />
                        <span className="text-sm font-medium">{t('admin.settings.changePassword')}</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
            
            {selectedMedia.length > 0 && (
              <div className="flex flex-wrap justify-start lg:justify-end items-center gap-2 mt-2 sm:mt-0 order-3 lg:order-none w-full lg:w-auto">
                <div className="flex flex-col items-end">
                <button
                  onClick={handleBulkDownload}
                  disabled={isBulkDownloading}
                  className={`bg-gradient-to-r from-blue-500 to-blue-400 hover:from-blue-600 hover:to-blue-500 text-white font-bold py-2 px-4 rounded-xl shadow flex items-center gap-2 transition-all duration-200 ${isBulkDownloading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {isBulkDownloading ? (
                    <>
                        {downloadProgress >= 100 ? (
                          <>
                            <FaCheck className="-ml-1 mr-3 h-5 w-5 text-white" />
                            <span className="flex items-center gap-2">
                              {t('admin.downloadComplete')}
                              <span className="bg-white bg-opacity-20 px-2 py-1 rounded text-xs font-mono">
                                ✓ 100%
                              </span>
                            </span>
                          </>
                        ) : (
                          <>
                            <FaSpinner className="ultra-spinner -ml-1 mr-3 h-5 w-5 text-white" />
                            <span className="flex items-center gap-2">
                              {downloadProgress > 0 
                                ? t('admin.downloadingWithProgress', { progress: downloadProgress })
                                : t('admin.downloading')
                              }
                              {downloadProgress > 0 && (
                                <span className="bg-white bg-opacity-20 px-2 py-1 rounded text-xs font-mono">
                                  {downloadProgress}%
                                </span>
                              )}
                            </span>
                          </>
                        )}
                    </>
                  ) : (
                    <>
                      <FaDownload className="h-5 w-5" />
                      {t('admin.downloadSelected', { count: selectedMedia.length })}
                    </>
                  )}
                </button>
                  
                  {/* Progress Bar */}
                  {isBulkDownloading && downloadProgress > 0 && (
                    <div className="w-full mt-2 bg-gray-200 rounded-full h-2 shadow-inner">
                      <div 
                        className={`h-2 rounded-full transition-all duration-300 ease-out ${
                          downloadProgress >= 100 
                            ? 'bg-gradient-to-r from-green-500 to-green-400' 
                            : 'bg-gradient-to-r from-blue-500 to-blue-400'
                        }`}
                        style={{ width: `${downloadProgress}%` }}
                      ></div>
                    </div>
                  )}
                </div>
                
                <button
                  onClick={handleBulkDelete}
                  className="bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white font-bold py-2 px-4 rounded-xl shadow flex items-center gap-2 transition-all duration-200"
                >
                  <FaTrash className="h-5 w-5" />
                  {t('admin.deleteSelected', { count: selectedMedia.length })}
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Modern Main Content */}
      <main className="max-w-7xl mx-auto py-8 px-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl mb-6 backdrop-blur-sm shadow-sm">
            <div className="flex items-center gap-3">
              <span className="text-red-500">⚠️</span>
            {error}
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-16">
            <div className="inline-block ultra-spinner rounded-full h-12 w-12 border-2 border-amber-500 border-t-transparent gpu-accelerated"></div>
            <p className="mt-4 text-gray-700 text-lg">{t('admin.loading')}</p>
          </div>
        ) : !hash ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-gradient-to-r from-amber-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
              <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd" />
              </svg>
            </div>
            <p className="text-gray-700 mb-2 text-lg">{t('admin.noHashMessage')}</p>
            <p className="text-gray-500 text-sm">{t('admin.noHashExample')}</p>
          </div>
        ) : mediaList.length === 0 ? (
          <div className="text-center py-16">
            <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg ${
              mediaType === 'audios' 
                ? 'bg-gradient-to-r from-rose-300 to-pink-400' 
                : mediaType === 'videos'
                  ? 'bg-gradient-to-r from-purple-400 to-indigo-500'
                  : 'bg-gradient-to-r from-blue-400 to-indigo-500'
            }`}>
              {mediaType === 'audios' ? (
                <FaMicrophone className="w-10 h-10 text-white" />
              ) : mediaType === 'videos' ? (
                <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 6a2 2 0 012-2h6l2 2h6a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
                </svg>
              ) : (
                <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                </svg>
              )}
            </div>
            <p className={`text-lg ${isDarkTheme ? 'text-gray-300' : 'text-gray-700'}`}>
              {mediaType === 'audios' 
                ? t('admin.emptyState.noVoiceNotes') 
                : mediaType === 'videos'
                  ? t('admin.emptyState.noVideos')
                  : t('admin.emptyState.noPhotos', 'No media found.')}
            </p>
            <p className={`text-sm mt-2 ${isDarkTheme ? 'text-gray-500' : 'text-gray-500'}`}>
              {mediaType === 'audios' 
                ? t('admin.emptyState.noVoiceNotesHint')
                : mediaType === 'videos'
                  ? t('admin.emptyState.noVideosHint')
                  : t('admin.emptyState.noPhotosHint')}
            </p>
            
            {/* Tab buttons for empty state */}
            <div className="flex justify-center mt-6">
              <div className="flex gap-2 items-center">
                <button
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-300 ${
                    mediaType === 'images' 
                      ? 'bg-pink-400 text-white' 
                      : isDarkTheme
                        ? 'bg-slate-700 text-gray-400 hover:bg-slate-600'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                  }`}
                  onClick={() => handleMediaTypeChange('images')}
                >
                  {t('admin.tabs.photos')}
                </button>
                <button
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-300 ${
                    mediaType === 'videos' 
                      ? 'bg-pink-400 text-white' 
                      : isDarkTheme
                        ? 'bg-slate-700 text-gray-400 hover:bg-slate-600'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                  }`}
                  onClick={() => handleMediaTypeChange('videos')}
                >
                  {t('admin.tabs.videos')}
                </button>
                <button
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-300 ${
                    mediaType === 'audios' 
                      ? 'bg-pink-400 text-white' 
                      : isDarkTheme
                        ? 'bg-slate-700 text-gray-400 hover:bg-slate-600'
                        : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                  }`}
                  onClick={() => handleMediaTypeChange('audios')}
                >
                  {t('admin.tabs.voiceNotes')}
                </button>
              </div>
            </div>
          </div>
        ) : (
              <div>
                              {/* Media Type Tabs */}
              <div className={`flex justify-center mb-6 backdrop-blur-sm rounded-2xl p-2 shadow-sm transition-all duration-300 ${isDarkTheme 
                ? 'bg-slate-950/60 border border-slate-800/50' 
                : 'bg-white/60 border border-stone-200/50'
              }`}>
                <div className="flex gap-2 items-center">
                  <button
                    className={`px-4 py-2 rounded-xl font-medium transition-all duration-300 flex items-center gap-2 ${
                      mediaType === 'images' 
                        ? 'bg-gradient-to-r from-rose-300 to-pink-400 text-white shadow-md' 
                        : isDarkTheme
                          ? 'bg-slate-800/60 text-gray-400 hover:bg-slate-700/60 hover:text-gray-200'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800'
                    }`}
                    onClick={() => handleMediaTypeChange('images')}
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                    </svg>
                    {t('admin.tabs.photos')}
                  </button>
                  <button
                    className={`px-4 py-2 rounded-xl font-medium transition-all duration-300 flex items-center gap-2 ${
                      mediaType === 'videos' 
                        ? 'bg-gradient-to-r from-rose-300 to-pink-400 text-white shadow-md' 
                        : isDarkTheme
                          ? 'bg-slate-800/60 text-gray-400 hover:bg-slate-700/60 hover:text-gray-200'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800'
                    }`}
                    onClick={() => handleMediaTypeChange('videos')}
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2 6a2 2 0 012-2h6l2 2h6a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
                    </svg>
                    {t('admin.tabs.videos')}
                  </button>
                  <button
                    className={`px-4 py-2 rounded-xl font-medium transition-all duration-300 flex items-center gap-2 ${
                      mediaType === 'audios' 
                        ? 'bg-gradient-to-r from-rose-300 to-pink-400 text-white shadow-md' 
                        : isDarkTheme
                          ? 'bg-slate-800/60 text-gray-400 hover:bg-slate-700/60 hover:text-gray-200'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800'
                    }`}
                    onClick={() => handleMediaTypeChange('audios')}
                  >
                    <FaMicrophone className="w-4 h-4" />
                    {t('admin.tabs.voiceNotes')}
                  </button>
                </div>
              </div>

              {/* Modern Controls Bar */}
              <div className={`flex flex-wrap justify-between items-center mb-8 backdrop-blur-sm rounded-2xl p-6 shadow-sm transition-all duration-300 ${isDarkTheme 
                ? 'bg-slate-950/60 border border-slate-800/50' 
                : 'bg-white/60 border border-stone-200/50'
              }`}>
                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <div className="relative">
                    <input
                      type="checkbox"
                        className="sr-only"
                      checked={selectedMedia.length === mediaList.length && mediaList.length > 0}
                      onChange={handleSelectAll}
                    />
                      <div className={`w-6 h-6 rounded-lg border-2 transition-all duration-200 ${
                        selectedMedia.length === mediaList.length && mediaList.length > 0
                          ? 'bg-gradient-to-r from-amber-500 to-orange-500 border-amber-500' 
                          : isDarkTheme
                            ? 'bg-slate-900/80 border-slate-600 group-hover:border-amber-400'
                            : 'bg-white/80 border-gray-400 group-hover:border-amber-400'
                      }`}>
                        {selectedMedia.length === mediaList.length && mediaList.length > 0 && (
                          <svg className="w-4 h-4 text-white absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    </div>
                    <span className={`font-medium transition-colors duration-300 ${isDarkTheme 
                      ? 'text-gray-300 group-hover:text-white' 
                      : 'text-gray-700 group-hover:text-gray-900'
                    }`}>
                      {t('admin.selectAll')}
                    </span>
                  </label>
                  
                  {selectedMedia.length > 0 && (
                    <div className={`px-4 py-2 rounded-lg border transition-all duration-300 ${isDarkTheme
                      ? 'bg-blue-900/50 border border-blue-800/60'
                      : 'bg-amber-100 border border-amber-200'
                    }`}>
                      <span className={`text-sm font-medium transition-colors duration-300 ${isDarkTheme 
                        ? 'text-blue-300' 
                        : 'text-amber-700'
                      }`}>
                        {selectedMedia.length} selected
                      </span>
                    </div>
                  )}
                </div>
                
                  <button
                    onClick={() => setSelectedMedia([])}
                    disabled={selectedMedia.length === 0}
                  className={`font-medium py-2 px-4 rounded-xl border transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${isDarkTheme
                    ? 'bg-slate-900 hover:bg-slate-800 text-gray-300 hover:text-white border-slate-700'
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800 border-gray-300'
                  }`}
                  >
                  <FaTimes className="h-4 w-4" />
                    {t('admin.clear')}
                  </button>
                </div>
                
                {/* Ultra-Smooth Masonry Grid */}
                <div className="masonry-container gpu-accelerated">
                  {mediaList.map((media) => (
                    <div 
                      key={media.id} 
                      className="masonry-item group"
                      data-id={media.id}
                      ref={(el) => {
                        if (!el) return;
                        const mediaEl = el.querySelector('img, video');
                        const noteEl = el.querySelector('.note-section');
                        if (mediaEl && noteEl) {
                          const resizeObserver = new ResizeObserver(entries => {
                            for (let entry of entries) {
                              noteEl.style.width = `${entry.contentRect.width}px`;
                            }
                          });
                          resizeObserver.observe(mediaEl);
                        }
                      }}
                    >
                      <div className={`masonry-card backdrop-blur-md rounded-2xl overflow-hidden smooth-hover hover:shadow-xl shadow-sm transition-all duration-300 flex flex-col ${isDarkTheme
                        ? 'bg-slate-950/80 border border-slate-800/60 hover:border-slate-700'
                        : 'bg-white/80 border border-stone-200/60 hover:border-stone-300'
                      }`}>
                        {/* Image/Video Container */}
                        <div className="relative overflow-hidden">
                          {/* Modern Checkbox */}
                          <div className="absolute top-3 left-3 z-20">
                          <label className="cursor-pointer">
                        <input
                          type="checkbox"
                                className="sr-only"
                          checked={selectedMedia.includes(media.id)}
                          onChange={() => handleSelectMedia(media.id)}
                        />
                              <div className={`w-6 h-6 rounded-lg border-2 backdrop-blur-sm smooth-transition ${
                                selectedMedia.includes(media.id)
                                  ? 'bg-gradient-to-r from-amber-500 to-orange-500 border-amber-500 shadow-lg' 
                                  : 'bg-white/70 border-gray-400 hover:border-amber-400'
                              }`}>
                                                                  {selectedMedia.includes(media.id) && (
                                    <svg className="w-4 h-4 text-white absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  )}
                              </div>
                            </label>
                          </div>
                          
                          {/* Media Content - Always Visible */}
                        {media.file_type.startsWith('image/') ? (
                          <>
                            <img
                              src={media.storage_url}
                              alt={media.file_name}
                              className="media-content object-cover"
                              loading="lazy"
                              decoding="async"
                            />
                            
                            {/* Overlay on Hover for Images */}
                            <div className="absolute inset-0 bg-gradient-to-t from-gray-900/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 smooth-transition pointer-events-none">
                              <div className="absolute bottom-4 left-4 right-4">
                                <p className="text-white text-sm font-medium truncate drop-shadow-lg">
                                  {media.file_name}
                                </p>
                                <p className="text-white/90 text-xs mt-1 drop-shadow-md">
                                  {(media.file_size / 1024 / 1024).toFixed(1)} MB
                                </p>
                              </div>
                            </div>

                            {/* Floating Action Buttons - Inside Image Container */}
                            <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 smooth-transition z-10">
                              <button
                                onClick={() => window.open(media.storage_url, '_blank')}
                                className="w-10 h-10 bg-green-500/40 backdrop-blur-sm hover:bg-green-500 rounded-full flex items-center justify-center smooth-hover shadow-lg gpu-accelerated"
                                title={t('admin.view')}
                              >
                                <FaEye className="h-4 w-4 text-white" />
                              </button>
                              <button
                                onClick={() => handleDownload(media.id)}
                                disabled={downloadingMedia === media.id}
                                className={`w-10 h-10 bg-blue-500/40 backdrop-blur-sm hover:bg-blue-500 rounded-full flex items-center justify-center smooth-hover shadow-lg gpu-accelerated ${downloadingMedia === media.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                                title={t('admin.download')}
                              >
                                {downloadingMedia === media.id ? (
                                  <FaSpinner className="ultra-spinner h-4 w-4 text-white" />
                                ) : (
                                  <FaDownload className="h-4 w-4 text-white" />
                                )}
                              </button>
                              <button
                                onClick={() => handleDelete(media.id)}
                                className="w-10 h-10 bg-red-500/40 backdrop-blur-sm hover:bg-red-500 rounded-full flex items-center justify-center smooth-hover shadow-lg gpu-accelerated"
                                title={t('admin.delete')}
                              >
                                <FaTrash className="h-4 w-4 text-white" />
                              </button>
                            </div>
                          </>
                        ) : media.file_type.startsWith('audio/') ? (
                          /* Audio Player Card */
                          <div className={`relative p-6 min-h-[200px] flex flex-col items-center justify-center ${isDarkTheme 
                            ? 'bg-gradient-to-br from-slate-800 via-slate-900 to-pink-900/30' 
                            : 'bg-gradient-to-br from-pink-50 via-white to-pink-100'
                          }`}>
                            {/* Modern Checkbox for Audio */}
                            <div className="absolute top-3 left-3 z-20">
                              <label className="cursor-pointer">
                                <input
                                  type="checkbox"
                                  className="sr-only"
                                  checked={selectedMedia.includes(media.id)}
                                  onChange={() => handleSelectMedia(media.id)}
                                />
                                <div className={`w-6 h-6 rounded-lg border-2 backdrop-blur-sm smooth-transition ${
                                  selectedMedia.includes(media.id)
                                    ? 'bg-gradient-to-r from-amber-500 to-orange-500 border-amber-500 shadow-lg' 
                                    : isDarkTheme
                                      ? 'bg-slate-700/70 border-gray-500 hover:border-amber-400'
                                      : 'bg-white/70 border-gray-400 hover:border-amber-400'
                                }`}>
                                  {selectedMedia.includes(media.id) && (
                                    <svg className="w-4 h-4 text-white absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  )}
                                </div>
                              </label>
                            </div>
                            
                            {/* Microphone Icon with Animation */}
                            <div className={`relative mb-4 ${audioPlayingId === media.id ? 'animate-pulse' : ''}`}>
                              <div className={`w-20 h-20 rounded-full flex items-center justify-center shadow-lg ${isDarkTheme 
                                ? 'bg-gradient-to-br from-rose-400 to-pink-500' 
                                : 'bg-gradient-to-br from-rose-300 to-pink-400'
                              }`}>
                                <FaMicrophone className="text-white text-3xl" />
                              </div>
                              {audioPlayingId === media.id && (
                                <div className="absolute inset-0 rounded-full border-4 border-rose-200 animate-ping opacity-50"></div>
                              )}
                            </div>
                            
                            {/* Sender Name */}
                            {media.note && (
                              <p className={`text-center font-semibold mb-2 ${isDarkTheme ? 'text-rose-300' : 'text-pink-500'}`}>
                                {media.note.split('|')[0] || media.note}
                              </p>
                            )}
                            
                            <p className={`text-xs mb-4 ${isDarkTheme ? 'text-gray-400' : 'text-gray-500'}`}>{t('admin.voiceNote')}</p>
                            
                            {/* Play/Pause Button */}
                            <button
                              onClick={() => toggleAudioPlay(media.id, media.storage_url)}
                              className={`w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110 active:scale-95 z-10 relative ${isDarkTheme
                                ? 'bg-gradient-to-br from-rose-400 to-pink-500 hover:from-rose-300 hover:to-pink-400'
                                : 'bg-gradient-to-br from-rose-300 to-pink-400 hover:from-rose-400 hover:to-pink-500'
                              }`}
                            >
                              {audioPlayingId === media.id ? (
                                <FaPause className="text-white text-lg" />
                              ) : (
                                <FaPlay className="text-white text-lg ml-1" />
                              )}
                            </button>
                            
                            {/* Action Buttons for Audio */}
                            <div className="flex gap-2 mt-4 z-10 relative">
                              <button
                                onClick={() => handleDownload(media.id)}
                                disabled={downloadingMedia === media.id}
                                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 ${isDarkTheme
                                  ? 'bg-blue-500/40 hover:bg-blue-500'
                                  : 'bg-blue-500/40 hover:bg-blue-500'
                                } ${downloadingMedia === media.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                                title={t('admin.download')}
                              >
                                {downloadingMedia === media.id ? (
                                  <FaSpinner className="ultra-spinner h-4 w-4 text-white" />
                                ) : (
                                  <FaDownload className="h-4 w-4 text-white" />
                                )}
                              </button>
                              <button
                                onClick={() => handleDelete(media.id)}
                                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200 ${isDarkTheme
                                  ? 'bg-red-500/40 hover:bg-red-500'
                                  : 'bg-red-500/40 hover:bg-red-500'
                                }`}
                                title={t('admin.delete')}
                              >
                                <FaTrash className="h-4 w-4 text-white" />
                              </button>
                            </div>
                          </div>
                        ) : (
                            <div className="relative group">
                          <video
                                ref={(el) => {
                                  if (el) {
                                    el.videoId = media.id;
                                  }
                                }}
                            src={media.storage_url}
                                className="media-content object-cover w-full h-auto"
                                preload="metadata"
                                muted
                                onLoadedMetadata={(e) => {
                                  // Seek to 1 second to get a good thumbnail
                                  e.target.currentTime = 1;
                                }}
                                onLoadedData={(e) => {
                                  // Show the video once it's loaded
                                  e.target.style.opacity = '1';
                                  e.target.currentTime = 1; // Seek to 1 second for thumbnail
                                  
                                  // Set initial volume icons based on muted state
                                  const volumeIcon = e.target.parentElement.querySelector('.volume-on-icon');
                                  const muteIcon = e.target.parentElement.querySelector('.volume-mute-icon');
                                  
                                  if (e.target.muted) {
                                    if (volumeIcon) volumeIcon.classList.add('hidden');
                                    if (muteIcon) muteIcon.classList.remove('hidden');
                                  } else {
                                    if (volumeIcon) volumeIcon.classList.remove('hidden');
                                    if (muteIcon) muteIcon.classList.add('hidden');
                                  }
                                }}
                                onTimeUpdate={(e) => {
                                  // Update custom progress bar
                                  const video = e.target;
                                  const progress = (video.currentTime / video.duration) * 100;
                                  const progressBar = video.parentElement.querySelector('.video-progress-fill');
                                  const timeDisplay = video.parentElement.querySelector('.video-time');
                                  
                                  if (progressBar) {
                                    progressBar.style.width = `${progress}%`;
                                  }
                                  
                                  if (timeDisplay) {
                                    const current = Math.floor(video.currentTime);
                                    const total = Math.floor(video.duration);
                                    const formatTime = (seconds) => {
                                      const mins = Math.floor(seconds / 60);
                                      const secs = seconds % 60;
                                      return `${mins}:${secs.toString().padStart(2, '0')}`;
                                    };
                                    timeDisplay.textContent = `${formatTime(current)} / ${formatTime(total)}`;
                                  }
                                }}
                                onPlay={(e) => {
                                  console.log('🎬 Video playing');
                                  e.target.classList.add('playing');
                                  const playOverlay = e.target.parentElement.querySelector('.play-overlay');
                                  const customControls = e.target.parentElement.querySelector('.custom-video-controls');
                                  
                                  // Toggle play/pause icons
                                  const playIcon = e.target.parentElement.querySelector('.play-icon');
                                  const pauseIcon = e.target.parentElement.querySelector('.pause-icon');
                                  if (playIcon) playIcon.classList.add('hidden');
                                  if (pauseIcon) pauseIcon.classList.remove('hidden');
                                  
                                  if (playOverlay) playOverlay.style.display = 'none';
                                  if (customControls) customControls.style.opacity = '1';
                                }}
                                onPause={(e) => {
                                  console.log('⏸️ Video paused');
                                  e.target.classList.remove('playing');
                                  const playOverlay = e.target.parentElement.querySelector('.play-overlay');
                                  
                                  // Toggle play/pause icons
                                  const playIcon = e.target.parentElement.querySelector('.play-icon');
                                  const pauseIcon = e.target.parentElement.querySelector('.pause-icon');
                                  if (playIcon) playIcon.classList.remove('hidden');
                                  if (pauseIcon) pauseIcon.classList.add('hidden');
                                  
                                  if (playOverlay) playOverlay.style.display = 'flex';
                                }}
                                onEnded={(e) => {
                                  console.log('🏁 Video ended');
                                  e.target.classList.remove('playing');
                                  e.target.currentTime = 0;
                                  const playOverlay = e.target.parentElement.querySelector('.play-overlay');
                                  const customControls = e.target.parentElement.querySelector('.custom-video-controls');
                                  
                                  // Toggle play/pause icons back to play
                                  const playIcon = e.target.parentElement.querySelector('.play-icon');
                                  const pauseIcon = e.target.parentElement.querySelector('.pause-icon');
                                  if (playIcon) playIcon.classList.remove('hidden');
                                  if (pauseIcon) pauseIcon.classList.add('hidden');
                                  
                                  if (playOverlay) playOverlay.style.display = 'flex';
                                  if (customControls) customControls.style.opacity = '0';
                                }}
                                style={{ opacity: 0, transition: 'opacity 0.3s ease' }}
                                onError={(e) => {
                                  // Fallback if video fails to load
                                  e.target.style.display = 'none';
                                  e.target.nextElementSibling.style.display = 'flex';
                                }}
                              />
                              
                              {/* Fallback if video doesn't load */}
                              <div 
                                className="absolute inset-0 bg-slate-100 flex items-center justify-center cursor-pointer group-hover:bg-slate-200 transition-all duration-300"
                                style={{ display: 'none' }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(media.storage_url, '_blank');
                                }}
                              >
                                <div className="text-center">
                                  <div className="w-10 h-10 bg-slate-500 rounded-lg flex items-center justify-center mx-auto mb-2">
                                    <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                      <path d="M2 6a2 2 0 012-2h6l2 2h6a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
                                    </svg>
                                  </div>
                                  <div className="text-slate-600 text-xs font-medium">Video</div>
                                  <div className="text-slate-500 text-xs mt-1">
                                    {(media.file_size / 1024 / 1024).toFixed(1)} MB
                                  </div>
                                </div>
                              </div>
                              

                              {/* Play button overlay */}
                              <div 
                                className="play-overlay absolute inset-0 bg-transparent hover:bg-black/20 transition-all duration-300 flex items-center justify-center cursor-pointer z-10"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  console.log('🎯 Play button clicked!');
                                  const video = e.currentTarget.parentElement.querySelector('video');
                                  if (video) {
                                    console.log('📹 Video found, paused:', video.paused);
                                    
                                    if (video.paused) {
                                      video.play().then(() => {
                                        console.log('▶️ Video play started successfully');
                                      }).catch(err => {
                                        console.error('❌ Video play failed:', err);
                                      });
                                    } else {
                                      video.pause();
                                      console.log('⏸️ Video paused');
                                    }
                                  } else {
                                    console.log('❌ Video not found!');
                                  }
                                }}
                              >
                                <div className="w-16 h-16 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-xl opacity-70 hover:opacity-100 transition-all duration-300 hover:scale-110">
                                  <svg className="w-8 h-8 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              </div>

                              {/* Custom Video Controls */}
                              <div className="custom-video-controls absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/60 to-transparent p-4 opacity-0 transition-all duration-300 z-20">
                                {/* Progress Bar */}
                                <div className="mb-3">
                                  <div 
                                    className="video-progress-bar w-full h-2 bg-white/30 rounded-full cursor-pointer group"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const video = e.currentTarget.closest('.relative').querySelector('video');
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      const clickX = e.clientX - rect.left;
                                      const progress = clickX / rect.width;
                                      video.currentTime = progress * video.duration;
                                      console.log(`⏭️ Seeking to: ${Math.floor(progress * 100)}%`);
                                    }}
                                  >
                                    <div className="video-progress-fill h-full bg-amber-500 rounded-full relative group-hover:bg-amber-400 transition-colors duration-200">
                                      <div className="absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full border-2 border-amber-500 opacity-0 group-hover:opacity-100 transition-opacity duration-200 shadow-lg"></div>
                                    </div>
                                  </div>
                                </div>

                                {/* Controls Row */}
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    {/* Play/Pause Button */}
                                    <button
                                      className="play-pause-btn w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const video = e.currentTarget.closest('.relative').querySelector('video');
                                        if (video.paused) {
                                          video.play();
                                        } else {
                                          video.pause();
                                        }
                                      }}
                                    >
                                      {/* Play Icon - shown when paused */}
                                      <svg className="play-icon w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                                      </svg>
                                      {/* Pause Icon - shown when playing */}
                                      <svg className="pause-icon w-5 h-5 text-white hidden" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                      </svg>
                                    </button>

                                    {/* Time Display */}
                                    <div className="video-time text-white text-sm font-medium">0:00 / 0:00</div>
                                  </div>

                                  <div className="flex items-center gap-2">
                                    {/* Volume Button */}
                                    <button
                                      className="volume-btn w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all duration-200"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const video = e.currentTarget.closest('.relative').querySelector('video');
                                        video.muted = !video.muted;
                                        
                                        // Toggle volume icons
                                        const volumeIcon = e.currentTarget.querySelector('.volume-on-icon');
                                        const muteIcon = e.currentTarget.querySelector('.volume-mute-icon');
                                        
                                        if (video.muted) {
                                          volumeIcon.classList.add('hidden');
                                          muteIcon.classList.remove('hidden');
                                        } else {
                                          volumeIcon.classList.remove('hidden');
                                          muteIcon.classList.add('hidden');
                                        }
                                        
                                        console.log(`🔊 Volume ${video.muted ? 'muted' : 'unmuted'}`);
                                      }}
                                    >
                                      {/* Volume On Icon - shown when unmuted */}
                                      <svg className="volume-on-icon w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 9v6M18 8v8" />
                                      </svg>
                                      {/* Volume Mute Icon - shown when muted */}
                                      <svg className="volume-mute-icon w-4 h-4 text-white hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                                      </svg>
                                    </button>

                                    {/* Fullscreen Button */}
                                    <button
                                      className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-all duration-200"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const video = e.currentTarget.closest('.relative').querySelector('video');
                                        if (video.requestFullscreen) {
                                          video.requestFullscreen();
                                          console.log('🖥️ Fullscreen requested');
                                        }
                                      }}
                                    >
                                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 11-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 010-2h4a1 1 0 011 1v4a1 1 0 01-2 0V6.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 5H12zm-9 7a1 1 0 012 0v1.586l2.293-2.293a1 1 0 111.414 1.414L6.414 15H8a1 1 0 010 2H4a1 1 0 01-1-1v-4zm13-1a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 010-2h1.586l-2.293-2.293a1 1 0 111.414-1.414L15 13.586V12a1 1 0 011-1z" clipRule="evenodd" />
                                      </svg>
                                    </button>
                                  </div>
                              </div>
                            </div>
                          
                          {/* Overlay on Hover - Only for Videos */}
                          <div className="absolute inset-0 bg-gradient-to-t from-gray-900/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 smooth-transition pointer-events-none">
                            <div className="absolute bottom-4 left-4 right-4">
                              <p className="text-white text-sm font-medium truncate drop-shadow-lg">
                          {media.file_name}
                              </p>
                              <p className="text-white/90 text-xs mt-1 drop-shadow-md">
                                {(media.file_size / 1024 / 1024).toFixed(1)} MB
                        </p>
                            </div>
                          </div>

                          {/* Floating Action Buttons - Inside Video Container */}
                          <div className="absolute top-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 smooth-transition z-10">
                            <button
                              onClick={() => window.open(media.storage_url, '_blank')}
                              className="w-10 h-10 bg-green-500/40 backdrop-blur-sm hover:bg-green-500 rounded-full flex items-center justify-center smooth-hover shadow-lg gpu-accelerated"
                              title={t('admin.view')}
                            >
                              <FaEye className="h-4 w-4 text-white" />
                            </button>
                            <button
                              onClick={() => handleDownload(media.id)}
                              disabled={downloadingMedia === media.id}
                              className={`w-10 h-10 bg-blue-500/40 backdrop-blur-sm hover:bg-blue-500 rounded-full flex items-center justify-center smooth-hover shadow-lg gpu-accelerated ${downloadingMedia === media.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                              title={t('admin.download')}
                            >
                              {downloadingMedia === media.id ? (
                                <FaSpinner className="ultra-spinner h-4 w-4 text-white" />
                              ) : (
                                <FaDownload className="h-4 w-4 text-white" />
                              )}
                            </button>
                            <button
                              onClick={() => handleDelete(media.id)}
                              className="w-10 h-10 bg-red-500/40 backdrop-blur-sm hover:bg-red-500 rounded-full flex items-center justify-center smooth-hover shadow-lg gpu-accelerated"
                              title={t('admin.delete')}
                            >
                              <FaTrash className="h-4 w-4 text-white" />
                            </button>
                          </div>
                            </div>
                        )}
                        </div>
                        {/* Modern Note Section */}
                        {media.note && (
                          <div className={`note-section p-4 transition-all duration-300 overflow-hidden ${isDarkTheme
                            ? 'bg-slate-900/80 border-t border-slate-700/60'
                            : 'bg-amber-50/80 border-t border-amber-200/60'
                          }`}>
                            <div className="w-full min-w-0">
                              {(() => {
                                const parts = media.note.split('|');
                                const hasNameAndNote = parts.length === 2 && parts[0].trim() && parts[1].trim();
                                const nameOnly = parts.length === 1 || (parts.length === 2 && parts[0].trim() && !parts[1].trim());
                                const noteOnly = parts.length === 2 && !parts[0].trim() && parts[1].trim();
                                
                                if (hasNameAndNote) {
                                  return (
                                    <div className="space-y-3">
                                      <div className="flex items-start gap-3 min-w-0">
                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors duration-300 ${isDarkTheme ? 'bg-slate-800' : 'bg-amber-100'}`}>
                                          <FaEdit className={`h-4 w-4 transition-colors duration-300 ${isDarkTheme ? 'text-blue-400' : 'text-amber-600'}`} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <p className={`text-sm break-words transition-colors duration-300 leading-relaxed word-wrap overflow-wrap-anywhere ${isDarkTheme ? 'text-gray-300' : 'text-gray-700'}`}>{parts[1].trim()}</p>
                                        </div>
                                      </div>
                                      <div className="text-right min-w-0">
                                        <span className={`text-xs font-medium transition-colors duration-300 break-words word-wrap overflow-wrap-anywhere ${isDarkTheme ? 'text-blue-400' : 'text-amber-600'}`}>— {parts[0].trim()}</span>
                                      </div>
                                    </div>
                                  );
                                } else if (nameOnly) {
                                  return (
                                    <div className="flex items-center gap-3 min-w-0">
                                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors duration-300 ${isDarkTheme ? 'bg-slate-800' : 'bg-amber-100'}`}>
                                        <span className={`text-sm transition-colors duration-300 ${isDarkTheme ? 'text-blue-400' : 'text-amber-600'}`}>👤</span>
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <span className={`text-sm font-medium transition-colors duration-300 break-words word-wrap overflow-wrap-anywhere ${isDarkTheme ? 'text-gray-300' : 'text-gray-700'}`}>{parts[0].trim()}</span>
                                      </div>
                                    </div>
                                  );
                                } else {
                                  const displayText = noteOnly ? parts[1].trim() : media.note;
                                  return (
                                    <div className="flex items-start gap-3 min-w-0">
                                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors duration-300 ${isDarkTheme ? 'bg-slate-800' : 'bg-amber-100'}`}>
                                        <FaEdit className={`h-4 w-4 transition-colors duration-300 ${isDarkTheme ? 'text-blue-400' : 'text-amber-600'}`} />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className={`text-sm break-words transition-colors duration-300 leading-relaxed word-wrap overflow-wrap-anywhere ${isDarkTheme ? 'text-gray-300' : 'text-gray-700'}`}>{displayText}</p>
                                      </div>
                                    </div>
                                  );
                                }
                              })()}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
        }
      </main>

      {/* Celebration Modal */}
      {showCelebrationModal && (
        <div 
          className="celebration-modal-overlay bg-black/60 backdrop-blur-sm"
          onClick={() => setShowCelebrationModal(false)}
        >
          <div className="celebration-modal-container">
            <div 
              className="celebration-modal bg-gradient-to-br from-pink-50 via-white to-amber-50 rounded-3xl shadow-2xl p-8 w-full"
              onClick={(e) => e.stopPropagation()}
            >
            {/* Close Button */}
            <button
              onClick={() => setShowCelebrationModal(false)}
              className="absolute top-4 right-4 w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-all duration-200 shadow-sm group z-10"
              title="Kapat"
            >
              <svg className="w-4 h-4 text-gray-600 group-hover:text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Header with Hearts Animation */}
            <div className="text-center mb-6">
              <div className="flex justify-center items-center gap-2 mb-4">
                <div className="animate-bounce">💕</div>
                <div className="animate-bounce-delay-100">🎉</div>
                <div className="animate-bounce-delay-200">💕</div>
              </div>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-pink-600 to-amber-600 bg-clip-text text-transparent">
                {t('admin.celebrationModal.title')}
              </h2>
              <p className="text-lg text-gray-700 mt-2 font-medium">
                {t('admin.celebrationModal.subtitle')}
              </p>
            </div>

            {/* Message */}
            <div className="text-center mb-6">
              <p className="text-gray-600 leading-relaxed">
                {t('admin.celebrationModal.message')}
              </p>
            </div>

            {/* Loading Animation */}
            <div className="text-center">
              <div className="flex justify-center items-center gap-2 mb-3">
                <div className="w-3 h-3 bg-gradient-to-r from-pink-500 to-amber-500 rounded-full animate-bounce"></div>
                <div className="w-3 h-3 bg-gradient-to-r from-pink-500 to-amber-500 rounded-full animate-bounce-delay-100"></div>
                <div className="w-3 h-3 bg-gradient-to-r from-pink-500 to-amber-500 rounded-full animate-bounce-delay-200"></div>
              </div>
              <p className="text-sm text-gray-500">
                {t('admin.celebrationModal.loading')}
              </p>
            </div>

            {/* Decorative Elements */}
            <div className="absolute top-16 left-4 text-2xl animate-spin-slow pointer-events-none">✨</div>
            <div className="absolute top-16 right-16 text-xl animate-pulse pointer-events-none">💫</div>
            <div className="absolute bottom-4 left-6 text-lg animate-bounce pointer-events-none">🌟</div>
            <div className="absolute bottom-6 right-16 text-2xl animate-spin-slow pointer-events-none">✨</div>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {showChangePasswordModal && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => {
            setShowChangePasswordModal(false);
            setOldPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setPasswordChangeMessage({ type: '', text: '' });
            setShowOldPassword(false);
            setShowNewPassword(false);
            setShowConfirmPassword(false);
          }}
        >
          <div 
            className={`w-full max-w-md rounded-2xl shadow-2xl p-6 ${isDarkTheme 
              ? 'bg-slate-900 border border-slate-700' 
              : 'bg-white border border-stone-200'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDarkTheme 
                  ? 'bg-slate-800' 
                  : 'bg-stone-100'
                }`}>
                  <FaKey className={`w-5 h-5 ${isDarkTheme ? 'text-slate-300' : 'text-slate-600'}`} />
                </div>
                <h2 className={`text-xl font-bold ${isDarkTheme ? 'text-slate-100' : 'text-slate-800'}`}>
                  {t('admin.changePassword.title')}
                </h2>
              </div>
              <button
                onClick={() => {
                  setShowChangePasswordModal(false);
                  setOldPassword('');
                  setNewPassword('');
                  setConfirmPassword('');
                  setPasswordChangeMessage({ type: '', text: '' });
                  setShowOldPassword(false);
                  setShowNewPassword(false);
                  setShowConfirmPassword(false);
                }}
                className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${isDarkTheme
                  ? 'hover:bg-slate-800 text-slate-400 hover:text-slate-200'
                  : 'hover:bg-stone-100 text-slate-500 hover:text-slate-700'
                }`}
              >
                <FaTimes className="w-4 h-4" />
              </button>
            </div>

            {/* Message Display */}
            {passwordChangeMessage.text && (
              <div className={`mb-4 p-3 rounded-lg text-sm ${
                passwordChangeMessage.type === 'success'
                  ? isDarkTheme 
                    ? 'bg-green-900/30 border border-green-700 text-green-300'
                    : 'bg-green-50 border border-green-200 text-green-700'
                  : isDarkTheme
                    ? 'bg-red-900/30 border border-red-700 text-red-300'
                    : 'bg-red-50 border border-red-200 text-red-700'
              }`}>
                {passwordChangeMessage.text}
              </div>
            )}

            {/* Form */}
            <div className="space-y-4">
              {/* Old Password */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkTheme ? 'text-slate-300' : 'text-slate-700'}`}>
                  {t('admin.changePassword.currentPassword')}
                </label>
                <div className="relative">
                  <input
                    type={showOldPassword ? "text" : "password"}
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                    className={`w-full px-4 py-2 pr-10 rounded-lg border transition-colors ${isDarkTheme
                      ? 'bg-slate-800 border-slate-700 text-slate-100 focus:border-slate-500'
                      : 'bg-white border-stone-300 text-slate-900 focus:border-stone-400'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                    placeholder={t('admin.changePassword.currentPasswordPlaceholder')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowOldPassword(!showOldPassword)}
                    className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${isDarkTheme ? 'text-slate-400' : 'text-slate-500'}`}
                  >
                    {showOldPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>

              {/* New Password */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkTheme ? 'text-slate-300' : 'text-slate-700'}`}>
                  {t('admin.changePassword.newPassword')}
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className={`w-full px-4 py-2 pr-10 rounded-lg border transition-colors ${isDarkTheme
                      ? 'bg-slate-800 border-slate-700 text-slate-100 focus:border-slate-500'
                      : 'bg-white border-stone-300 text-slate-900 focus:border-stone-400'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                    placeholder={t('admin.changePassword.newPasswordPlaceholder')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${isDarkTheme ? 'text-slate-400' : 'text-slate-500'}`}
                  >
                    {showNewPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${isDarkTheme ? 'text-slate-300' : 'text-slate-700'}`}>
                  {t('admin.changePassword.confirmPassword')}
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={`w-full px-4 py-2 pr-10 rounded-lg border transition-colors ${isDarkTheme
                      ? 'bg-slate-800 border-slate-700 text-slate-100 focus:border-slate-500'
                      : 'bg-white border-stone-300 text-slate-900 focus:border-stone-400'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                    placeholder={t('admin.changePassword.confirmPasswordPlaceholder')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${isDarkTheme ? 'text-slate-400' : 'text-slate-500'}`}
                  >
                    {showConfirmPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowChangePasswordModal(false);
                  setOldPassword('');
                  setNewPassword('');
                  setConfirmPassword('');
                  setPasswordChangeMessage({ type: '', text: '' });
                  setShowOldPassword(false);
                  setShowNewPassword(false);
                  setShowConfirmPassword(false);
                }}
                className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${isDarkTheme
                  ? 'bg-slate-800 hover:bg-slate-700 text-slate-300'
                  : 'bg-stone-100 hover:bg-stone-200 text-slate-700'
                }`}
              >
                {t('admin.changePassword.cancel')}
              </button>
              <button
                onClick={async () => {
                  // Validation
                  if (!oldPassword || !newPassword || !confirmPassword) {
                    setPasswordChangeMessage({ type: 'error', text: t('admin.changePassword.allFieldsRequired') });
                    return;
                  }
                  if (newPassword.length < 4) {
                    setPasswordChangeMessage({ type: 'error', text: t('admin.changePassword.passwordTooShort') });
                    return;
                  }
                  if (newPassword !== confirmPassword) {
                    setPasswordChangeMessage({ type: 'error', text: t('admin.changePassword.passwordsDoNotMatch') });
                    return;
                  }

                  setIsChangingPassword(true);
                  setPasswordChangeMessage({ type: '', text: '' });

                  try {
                    const response = await axiosInstance.post('/api/auth/change-password', {
                      oldPassword: oldPassword,
                      newPassword: newPassword
                    });

                    if (response.data.success) {
                      setPasswordChangeMessage({ type: 'success', text: t('admin.changePassword.success') });
                      setOldPassword('');
                      setNewPassword('');
                      setConfirmPassword('');
                      setShowOldPassword(false);
                      setShowNewPassword(false);
                      setShowConfirmPassword(false);
                      
                      // Close modal after 2 seconds
                      setTimeout(() => {
                        setShowChangePasswordModal(false);
                        setPasswordChangeMessage({ type: '', text: '' });
                      }, 2000);
                    } else {
                      setPasswordChangeMessage({ type: 'error', text: response.data.error || t('admin.changePassword.error') });
                    }
                  } catch (error) {
                    console.error('Error changing password:', error);
                    const errorMessage = error.response?.data?.error === 'Current password is incorrect' 
                      ? t('admin.changePassword.incorrectPassword')
                      : error.response?.data?.error || t('admin.changePassword.error');
                    setPasswordChangeMessage({ 
                      type: 'error', 
                      text: errorMessage
                    });
                  } finally {
                    setIsChangingPassword(false);
                  }
                }}
                disabled={isChangingPassword}
                className={`flex-1 px-4 py-2 rounded-lg font-medium text-white transition-colors flex items-center justify-center gap-2 ${
                  isChangingPassword
                    ? 'bg-blue-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {isChangingPassword ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    {t('admin.changePassword.changing')}
                  </>
                ) : (
                  t('admin.changePassword.submit')
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App; 